import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, onSnapshot, where, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/config';
import LoadingSpinner from '../../components/common/LoadingSpinner';
// --- NEW: Imported the ExclamationIcon to use as an info icon ---
import { ExclamationIcon } from '../../components/icons/Icons';

// A dedicated row component to encapsulate row logic
function ReportRow({ report, onSelectReport }) {
    const getStatusBadgeColor = (status) => {
        switch (status) {
            case 'Pending Approval': return 'bg-yellow-100 text-yellow-800';
            case 'Approved': return 'bg-green-100 text-green-800';
            case 'Rejected':
            case 'Rejected by Finance': 
		return 'bg-red-100 text-red-800';
            case 'Reimbursed': return 'bg-blue-100 text-blue-800';
            case 'Draft': return 'bg-gray-100 text-gray-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

// --- MODIFIED: This logic now finds the reason for either rejection type ---
    const rejectionReason = 
        report.status === 'Rejected' ? report.approvalComments :
        report.status === 'Rejected by Finance' ? report.financeRejectionReason : null;

    return (
        <tr 
            key={report.id}
            className="hover:bg-gray-50 cursor-pointer"
            onClick={() => onSelectReport(report)}
        >
            <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-600">{report.reportNumber}</td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{report.createdAt?.toDate().toLocaleDateString() || 'N/A'}</td>
            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-[#213368]">{report.reportName}</td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 font-medium">{`${report.reimbursementCurrency} ${report.totalAmount.toFixed(2)}`}</td>
            <td className="px-6 py-4 whitespace-nowrap text-sm">
                <div className="flex items-center space-x-2">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeColor(report.status)}`}>
                        {report.status}
                    </span>
                    {/* --- NEW: Info icon with tooltip for rejection reason --- */}
                    {rejectionReason && (
                        <div className="relative group">
                            <ExclamationIcon className="h-4 w-4 text-red-500" />
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-max max-w-xs bg-gray-800 text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10">
                                {rejectionReason}
                            </div>
                        </div>
                    )}
                </div>
            </td>
        </tr>
    );
}


// This component is the user's dashboard for viewing their own expense reports.
export default function MyExpenseReports({ userProfile, onSelectReport, appId }) {
    const [myReports, setMyReports] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'descending' });
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    useEffect(() => {
        if (!userProfile) {
            setIsLoading(false);
            return;
        }
        
        const reportsQuery = query(
            collection(db, `/artifacts/${appId}/public/data/expenseReports`),
            where("requesterId", "==", userProfile.uid),
            orderBy('createdAt', 'desc')
        );

        const unsubscribe = onSnapshot(reportsQuery, (snapshot) => {
            const reportsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setMyReports(reportsData);
            setIsLoading(false);
        });

        return () => unsubscribe();
    }, [userProfile, appId]);

    const processedReports = useMemo(() => {
        let filtered = [...myReports];

        if (searchTerm) {
            const lowercasedTerm = searchTerm.toLowerCase();
            filtered = filtered.filter(report =>
                report.reportName.toLowerCase().includes(lowercasedTerm) ||
                report.status.toLowerCase().includes(lowercasedTerm) ||
                report.reportNumber?.toLowerCase().includes(lowercasedTerm)
            );
        }

        filtered.sort((a, b) => {
            let aValue = a[sortConfig.key];
            let bValue = b[sortConfig.key];
            if (aValue?.toDate) aValue = aValue.toDate();
            if (bValue?.toDate) bValue = bValue.toDate();

            if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
            return 0;
        });

        return filtered;
    }, [myReports, searchTerm, sortConfig]);

    const paginatedReports = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return processedReports.slice(startIndex, startIndex + itemsPerPage);
    }, [processedReports, currentPage, itemsPerPage]);

    const totalPages = Math.ceil(processedReports.length / itemsPerPage);

    const handlePageChange = (newPage) => {
        if (newPage > 0 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const handleItemsPerPageChange = (event) => {
        setItemsPerPage(Number(event.target.value));
        setCurrentPage(1);
    };

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
        setCurrentPage(1);
    };

    const requestSort = (key) => {
        let direction = sortConfig.key === key && sortConfig.direction === 'ascending' ? 'descending' : 'ascending';
        setSortConfig({ key, direction });
        setCurrentPage(1);
    };
    
    if (isLoading) return <LoadingSpinner />;

    return (
        <div>
            <h2 className="text-2xl font-semibold text-[#213368] mb-4">My Expense Reports</h2>
            
            <div className="mb-4">
                <input
                    type="text"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    placeholder="Search by report ID, name, or status..."
                    className="w-full p-2 border border-gray-300 rounded-lg shadow-sm"
                />
            </div>

            <div className="bg-white shadow-md rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th onClick={() => requestSort('reportNumber')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Report ID</th>
                                <th onClick={() => requestSort('createdAt')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Date Submitted</th>
                                <th onClick={() => requestSort('reportName')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Report Name</th>
                                <th onClick={() => requestSort('totalAmount')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Total Amount</th>
                                <th onClick={() => requestSort('status')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Status</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {paginatedReports.length > 0 ? (
                                paginatedReports.map(report => (
                                    <ReportRow 
                                        key={report.id}
                                        report={report}
                                        onSelectReport={onSelectReport}
                                    />
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className="text-center py-12 px-6">
                                        <h3 className="text-sm font-medium text-[#213368]">No Expense Reports Found</h3>
                                        <p className="mt-1 text-sm text-gray-500">
                                            {searchTerm ? 'Your search returned no results.' : 'You have not created any expense reports yet.'}
                                        </p>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            
            {totalPages > 1 && (
                <div className="flex justify-between items-center mt-4">
                    <div className="flex items-center space-x-2 text-sm">
                        <span>Items per page:</span>
                        <select value={itemsPerPage} onChange={handleItemsPerPageChange} className="border border-gray-300 rounded-md p-1">
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                    </div>
                    <div className="flex items-center space-x-2">
                        <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="px-3 py-1 border rounded-md disabled:opacity-50">
                            Previous
                        </button>
                        <span className="text-sm">Page {currentPage} of {totalPages}</span>
                        <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="px-3 py-1 border rounded-md disabled:opacity-50">
                            Next
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

